import type { ObjectId } from 'mongodb'
import * as MsftGraph from '@microsoft/microsoft-graph-types'
import { ConversationReference } from 'botframework-schema'
import { BaseEditor } from 'slate'
import { ReactEditor } from 'slate-react'
import { HistoryEditor } from 'slate-history'

export type Option<T> = T | undefined
export type Nullable<T> = T | null

export type Id = string

interface Identified {
  id: Id
}

interface DBIdentified {
  _id: ObjectId
}

export enum SubscriptionPlanType {
  Free = 'free',
  Pro = 'pro',
  Team = 'team',
  Gift = 'gift',
}

export enum SubscriptionSource {
  Manual = 'manual',
  Marketplace = 'marketplace',
  TeamAssigned = 'team_assigned',
}

export interface SubscriptionData {
  planType: SubscriptionPlanType
  source: SubscriptionSource
  startDate: Date
  expiryDate: Date
  marketplaceSubscriptionId?: string
  marketplacePlanId?: string
  lastOperationId?: string
  quantity?: number // Used for team subscriptions to store seat count
  features: string[]
}

export interface SubscriptionDBO extends DBIdentified, SubscriptionData {
  accountId: ObjectId
}

export interface TeamSubscriptionDBO extends DBIdentified {
  name: string
  ownerAccountId: ObjectId
  planType: SubscriptionPlanType
  source: SubscriptionSource
  totalSeats: number
  startDate: Date
  expiryDate: Date
  marketplaceSubscriptionId?: string
  marketplacePlanId?: string
  seatAssignments: Array<{
    email: string
    accountId?: ObjectId
    assignedDate: Date
  }>
  features: string[]
}
type UserStatus = 'deleted'

interface UserData {
  name: string
  email: string
  emailVerified: Nullable<Date>
  status?: UserStatus
  statusUpdatedAt?: number
  // Onboarding milestone tracking
  onboardingNudgeSentAt?: Date
  firstReminderCongratsSentAt?: Date
  templatesOfferSentAt?: Date
  // User preferences
  engagementOptOut?: boolean
}

export interface UserDBO extends DBIdentified, UserData {}
export interface User extends Identified, UserData {}

export type IUserSummary = Pick<UserDBO, '_id' | 'email'>

interface Trigger<DateType> {
  triggerDate: DateType
}

// Target can be a Microsoft Graph chat, an error response, or null
export type Target =
  | MsftGraph.Chat
  | { status: number; info: any } // Error response from Graph API
  | null

export enum DelayedMessageStatus {
  Pending = 'pending',
  Processing = 'processing', // New status to prevent concurrent processing
  Sent = 'sent',
  Deleted = 'deleted',
  Error = 'error',
}

export interface Attachment {
  id: string
  filename: string
  contentType: string
  size: number
  url: string
  createdAt: string | Date // Can be either Date (server-side) or string (client-side)
}

interface DelayedMessageData<OID> {
  message: string
  target: Target
  userId: OID
  status: DelayedMessageStatus
  rrule?: string // iCalendar RRULE string for recurring reminders
  originalId?: string // For tracking instances of a recurring reminder
  attachments?: Attachment[] // New field for file attachments
  messageUrl?: string // URL to the original Teams message
}

export interface DelayedMessageDBO
  extends DBIdentified,
    Trigger<Date>,
    DelayedMessageData<ObjectId> {}
export interface DelayedMessage
  extends Identified,
    Trigger<string>,
    DelayedMessageData<string> {}

export interface AccountDBO extends DBIdentified {
  provider: string
  type: string
  providerAccountId: string
  token_type: string
  scope: string
  expires_at: number
  issued_at?: number
  request_login?: boolean
  ext_expires_in: number
  access_token: string
  id_token: string
  refresh_token?: string
  session_state: string
  userId: ObjectId
}

export interface ODataArray<T> {
  '@odata.context': string
  '@odata.count': number
  '@odata.nextLink': string
  value: T[]
}

export type ODataUnique<T> = T & {
  '@odata.context': string
}

export interface BotConversationDBO extends DBIdentified {
  reference: Partial<ConversationReference>
  graphUserId: string
}

export interface ListParams {
  offset?: number
  limit?: number
}

export type QueryParamsObj = Record<string, string | string[]>

export type MessageCountsByType = Record<DelayedMessageStatus, number>

export interface UserMetrics {
  messages: MessageCountsByType
  request_login: boolean
}

export interface UserWithMetricsDBO extends UserDBO, UserMetrics {}
export interface UserWithMetrics extends User, UserMetrics {}

interface EmailTrackingData {
  templateId: string
  name: string
  userId: ObjectId
}

export interface EmailTrackingDBO extends DBIdentified, EmailTrackingData {}

// Slate Editor Types
export type CustomEditor = BaseEditor & ReactEditor & HistoryEditor

export enum ActivityLogType {
  SubscriptionCreated = 'subscription_created',
  SubscriptionExpired = 'subscription_expired',
  SubscriptionModified = 'subscription_modified',
  SubscriptionReassignedFrom = 'subscription_reassigned_from',
  SubscriptionReassignedTo = 'subscription_reassigned_to',
  SubscriptionRenewed = 'subscription_renewed',
  SubscriptionRemoved = 'subscription_removed',
  TeamAssigned = 'team_assigned',
  TeamRemoved = 'team_removed',
  MessageSent = 'message_sent',
  EmailSent = 'email_sent',
  BulkMessageSent = 'bulk_message_sent',
  
  // Authentication and account related
  AccountCreated = 'account_created',
  AccountDeleted = 'account_deleted',
  UserLoggedIn = 'user_logged_in',
  UserLoggedOut = 'user_logged_out',
  
  // Error related
  MarketplaceTokenError = 'marketplace_token_error',
  PaymentError = 'payment_error',
  APIError = 'api_error',
  AuthenticationError = 'authentication_error',
  
  // Feature usage
  FeatureUsed = 'feature_used',
  SettingsUpdated = 'settings_updated',
  MessageScheduled = 'message_scheduled',
  MessageCancelled = 'message_cancelled',
  MessageEdited = 'message_edited',
  AttachmentUploaded = 'attachment_uploaded',
}

export interface ActivityLogData {
  userId: ObjectId
  email: string
  type: ActivityLogType
  timestamp: Date
  details: Record<string, any>
}

export interface ActivityLogDBO extends DBIdentified, ActivityLogData {}
export interface ActivityLog extends Identified, Omit<ActivityLogData, 'userId'> {
  userId: string
  timestamp: string
}

export enum BulkMessageTaskStatus {
  Pending = 'pending',
  InProgress = 'in_progress',
  Completed = 'completed',
  Failed = 'failed'
}

export interface BulkMessageTaskData {
  messageId: string             // Message ID - either user-provided or auto-generated
  title: string
  message: string
  createdAt: Date
  updatedAt: Date
  status: BulkMessageTaskStatus
  imageUrl?: string
  linkUrl?: string
  linkText?: string
  createdBy: string // Admin's email
  filter: {
    emails: string[] | null // null means all users
  }
  progress: {
    total: number
    processed: number
    succeeded: number
    failed: number
    currentBatch: number
    lastProcessedUserId?: string
  }
  error?: string
  processedUserIds?: string[]  // List of user IDs who have already received this message
}

export interface BulkMessageTaskDBO extends DBIdentified, BulkMessageTaskData {}
export interface BulkMessageTask extends Identified, Omit<BulkMessageTaskData, 'createdAt' | 'updatedAt'> {
  createdAt: string
  updatedAt: string
}

export enum MessageType {
  Teams = 'teams',
  Email = 'email',
}

export enum MessageDeliveryStatus {
  Pending = 'pending',
  Sent = 'sent',
  Failed = 'failed',
}

export interface MessageHistoryData {
  messageId: string              // Unique message ID (UUID)
  bulkMessageTaskId?: string     // Reference to bulk task if this was part of a bulk send
  delayedMessageId?: string      // Reference to delayed message if this was a scheduled message
  type: MessageType              // Teams or Email
  userId: ObjectId               // User who received the message
  email: string                  // Email of the recipient
  subject?: string               // Subject (for email) or title (for Teams)
  content: string                // Message content/body
  status: MessageDeliveryStatus  // Delivery status
  sentAt: Date                   // When the message was sent
  receivedAt?: Date              // When the message was confirmed delivered (if available)
  retryCount?: number            // Number of delivery attempts
  errorDetails?: string          // Details if delivery failed
  metadata: Record<string, any>  // Any additional metadata
}

export interface MessageHistoryDBO extends DBIdentified, MessageHistoryData {}

export interface MessageHistory extends Identified, Omit<MessageHistoryData, 'userId' | 'sentAt' | 'receivedAt'> {
  userId: string
  sentAt: string
  receivedAt?: string
}

export type BlockElement = {
  type:
    | 'paragraph'
    | 'block-quote'
    | 'heading-one'
    | 'heading-two'
    | 'list-item'
    | 'unordered-list'
    | 'ordered-list'
  children: (CustomText | LinkElement)[]
}

export type LinkElement = {
  type: 'link'
  children: CustomText[]
  url: string
}

export type CustomElement = BlockElement | LinkElement

export type CustomText = {
  text: string
  bold?: true
  italic?: true
  underline?: true
  code?: true
}

declare module 'slate' {
  interface CustomTypes {
    Editor: CustomEditor
    Element: CustomElement
    Text: CustomText
  }
}

// Message Templates
export interface MessageTemplateData {
  name: string
  content: string
  description?: string
  targetType?: string
  isPublic?: boolean // True if template is shared with the team
  createdAt: Date
  updatedAt: Date
  attachments?: Attachment[]
  tags?: string[]
}

export interface MessageTemplateDBO extends DBIdentified, MessageTemplateData {
  userId: ObjectId
}

export interface MessageTemplate extends Identified, Omit<MessageTemplateData, 'createdAt' | 'updatedAt'> {
  userId: string
  createdAt: string
  updatedAt: string
}

export interface EngagementMessageData {
  userId: ObjectId
  messageId: string
  sentAt: Date
  lastMessageSent: Date
}

export interface EngagementMessageDBO extends DBIdentified, EngagementMessageData {}

export interface UserSentMessageData {
  userId: ObjectId
  messageId: string
  sentAt: Date
}

export interface UserSentMessageDBO extends DBIdentified, UserSentMessageData {}
